import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import { Clock, MessageSquare, Share2 } from "lucide-react";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { ReactComponent as AskNavigatorIcon } from "assets/img/icons/ask-navigator.svg";
import { ReactComponent as SubmitIcon } from "assets/img/icons/submit.svg";
import { Button, Spinner, TextArea } from "components/elements";
import { MarkdownContent, ProseDocument } from "components/elements";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "components/shad-components/ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "components/shad-components/ui/toggle-group";
import { formatISODateRelative } from "lib/formatISODate";
import { trpc } from "lib/trpc";
import { VendorDetailedEntry } from "screens/vendors/VendorDetailedEntry";

type DiscussionEntry = {
    authorId: number;
    authorName?: string | null;
    postId: number;
    postTitle?: string | null;
    commentId: number;
};

type ArticleEntry = {
    id: number;
    title: string;
    url: string;
    publishedBy?: string | null;
    publishedAt?: string | null;
    excerpt?: string | null;
};

type VendorEntry = {
    id: number;
    name: string;
    website?: string | null;
    category?: string | null;
    notes?: string | null;
};

type PostEntry = {
    id: number;
    title?: string | null;
    content?: string | null;
    authorId: number;
    authorName?: string | null;
    createdAt: string;
    isAnonymous: boolean;
};

type PostCommentEntry = {
    id: number;
    content: string;
    postId: number;
    postTitle?: string | null;
    authorId: number;
    authorName?: string | null;
    createdAt: string;
    isAnonymous: boolean;
};

type VendorCommentEntry = {
    id: number;
    content: string;
    vendorId: number;
    vendorName?: string | null;
    authorId: number;
    authorName?: string | null;
    createdAt: string;
    isAnonymous: boolean;
};

type BlogPostEntry = {
    id: number;
    slug: string;
    title: string;
    excerpt?: string | null;
    previewKind: string;
    previewUrl?: string | null;
    previewAlt?: string | null;
    previewViewCount?: string | null;
    previewDurationSeconds?: number | null;
    publishedAt?: string | null;
    categories: Array<{ slug: string; name: string }>;
    authorName?: string | null;
};

type EventEntry = {
    id: number;
    title: string;
    description?: string | null;
    startDate: string;
    endDate?: string | null;
    location?: string | null;
    host?: string | null;
    type: string;
};

type NavigatorResponse = {
    queryType: "conversation" | "vendor_info" | "news" | "event" | "general";
    relatedKeywords: string[] | null;
    timeFrame?: {
        start?: string | null;
        end?: string | null;
        relativeString?: string | null;
    } | null;
    conversations?: {
        firstName?: string | null;
        lastName?: string | null;
        keywords?: string[] | null;
        discussions: DiscussionEntry[];
        posts: PostEntry[];
        postComments: PostCommentEntry[];
    } | null;
    vendorParams?: {
        vendorName?: string | null;
        criteria?: string[] | null;
        vendors: VendorEntry[];
        vendorComments: VendorCommentEntry[];
    } | null;
    newsParams?: {
        keywords?: string[] | null;
        source?: string | null;
        author?: string | null;
        articles: ArticleEntry[];
    } | null;
    eventParams?: {
        eventTitle?: string | null;
        eventType?: "COMPASS" | "EXTERNAL" | "HOLIDAY" | "OTHER" | null;
        host?: string | null;
        location?: string | null;
        category?: string | null;
        keywords?: string[] | null;
        events: EventEntry[];
    } | null;
    blogPosts: BlogPostEntry[];
    answerJson?: TipTapDoc | null;
    markdownAnswer?: string | null;
    suggestedQuestions?: string[] | null;
    contextResults: Array<{
        entityType:
            | "VENDOR"
            | "POST"
            | "BLOG_POST"
            | "FEED_ARTICLE"
            | "VENDOR_COMMENT"
            | "POST_COMMENT"
            | "EVENT"
            | "INTERNET_RESEARCH";
        id: string;
        name: string;
    }>;
    internetResearchResults: Array<{
        url: string;
        title: string;
        snippet: string;
        source: string;
    }>;
};

type SavedSearch = {
    id: number;
    question: string;
    scope: "dataset" | "internet" | "both";
    queryType: "conversation" | "vendor_info" | "news" | "event" | "general";
    answerJson?: TipTapDoc | null;
    markdownAnswer?: string | null;
    relatedKeywords: string[] | null;
    timeFrame: {
        start?: string | null;
        end?: string | null;
        relativeString?: string | null;
    } | null;
    // Stored as GeminiStructuredOutput params (no arrays)
    conversationParams: {
        firstName?: string | null;
        lastName?: string | null;
        keywords?: string[] | null;
    } | null;
    vendorParams: {
        vendorName?: string | null;
        criteria?: string[] | null;
    } | null;
    newsParams: {
        keywords?: string[] | null;
        source?: string | null;
        author?: string | null;
    } | null;
    eventParams: {
        eventTitle?: string | null;
        eventType?: "COMPASS" | "EXTERNAL" | "HOLIDAY" | "OTHER" | null;
        host?: string | null;
        location?: string | null;
        category?: string | null;
        keywords?: string[] | null;
    } | null;
    contextResults: NavigatorResponse["contextResults"];
    internetResearchResults: NavigatorResponse["internetResearchResults"];
    askedAt: string;
    createdAt: string;
};

const suggestions = [
    "How are Financial Advisors using AI to run their practices?",
    "What is the average pay for a Financial Advisor?",
    "What is the best software out there for compliance?",
    "What CRM softwares do financial advisors use?",
    "What is the average rate that Financial Advisors charge their clients?",
    "Are there any RIA conferences I should know about?",
    "How do Financial Advisors find clients?",
    "What are the top issues in the economy I should know about?",
    "How much is my company worth?",
    "Should I merge with another company?",
];

export const AskNavigator = (_props: { trigger?: React.ReactNode } = {}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [askScope, setAskScope] = useState<"dataset" | "internet" | "both">("dataset");
    const [question, setQuestion] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [response, setResponse] = useState<NavigatorResponse | null>(null);
    const [askedQuestion, setAskedQuestion] = useState<string | null>(null);
    const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);

    // State for share mode
    const [isShareMode, setIsShareMode] = useState(false);
    const [email, setEmail] = useState("");
    const [recipient, setRecipient] = useState("");
    const [emailError, setEmailError] = useState("");

    const askNavigator = trpc.askNavigator.ask.useMutation();
    const { data: vendorData } = trpc.vendors.getVendor.useQuery(selectedVendorId ?? 0, {
        enabled: selectedVendorId !== null,
    });
    const shareAnswerMutation = trpc.askNavigator.shareAnswer.useMutation();

    const { data: latestSearches } = trpc.askNavigator.getLatestSearches.useQuery({ limit: 5 });

    const handleSubmit = async (text: string) => {
        // Cancel any previous request
        abortController?.abort();
        const controller = new AbortController();
        setAbortController(controller);
        setAskedQuestion(text);
        setResponse(null);

        try {
            const result = await askNavigator.mutateAsync({
                question: text,
                scope: askScope,
            });
            // Check if aborted before setting response
            if (!controller.signal.aborted) {
                setResponse(result);
            }
        } catch (err: unknown) {
            if ((err instanceof Error && err.name === "AbortError") || controller.signal.aborted) {
                // cancel by resetting the mutation
                askNavigator.reset();
                console.log("Ask Navigator request aborted");
            } else {
                console.error("Error in Ask Navigator:", err);
            }
        } finally {
            setAbortController(null);
            setQuestion("");
        }
    };

    const handleSuggestionClick = (suggestion: string) => {
        handleSubmit(suggestion);
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (question.trim()) {
            handleSubmit(question.trim());
        }
    };

    const handleRestoreSearch = (saved: SavedSearch) => {
        const restored: NavigatorResponse = {
            queryType: saved.queryType,
            relatedKeywords: saved.relatedKeywords,
            timeFrame: saved.timeFrame ?? null,
            conversations: saved.conversationParams
                ? {
                      firstName: saved.conversationParams.firstName,
                      lastName: saved.conversationParams.lastName,
                      keywords: saved.conversationParams.keywords,
                      discussions: [],
                      posts: [],
                      postComments: [],
                  }
                : null,
            vendorParams: saved.vendorParams
                ? {
                      vendorName: saved.vendorParams.vendorName,
                      criteria: saved.vendorParams.criteria,
                      vendors: [],
                      vendorComments: [],
                  }
                : null,
            newsParams: saved.newsParams
                ? {
                      keywords: saved.newsParams.keywords,
                      source: saved.newsParams.source,
                      author: saved.newsParams.author,
                      articles: [],
                  }
                : null,
            eventParams: saved.eventParams
                ? {
                      eventTitle: saved.eventParams.eventTitle,
                      eventType: saved.eventParams.eventType,
                      host: saved.eventParams.host,
                      location: saved.eventParams.location,
                      category: saved.eventParams.category,
                      keywords: saved.eventParams.keywords,
                      events: [],
                  }
                : null,
            blogPosts: [],
            answerJson: saved.answerJson ?? null,
            markdownAnswer: saved.markdownAnswer,
            contextResults: saved.contextResults,
            internetResearchResults: saved.internetResearchResults,
        };
        setAskScope(saved.scope);
        setAskedQuestion(saved.question);
        setResponse(restored);
        setQuestion("");
    };

    const handleShareByEmail = async () => {
        if (!response || !askedQuestion) return;

        // Validate email
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!email.trim()) {
            setEmailError("Email is required.");
            return;
        }
        if (!emailRegex.test(email)) {
            setEmailError("Invalid email address.");
            return;
        }
        setEmailError("");

        try {
            // Prefer the live answerJson from the response; if a saved search
            // doesn't carry it for some reason we fall back to wrapping the
            // plaintext sidecar in a single paragraph so the email still
            // ships with a body rather than failing the validator.
            const answerJsonForShare =
                response.answerJson ??
                ({
                    type: "doc" as const,
                    content: [
                        {
                            type: "paragraph" as const,
                            content: [
                                { type: "text" as const, text: response.markdownAnswer ?? "" },
                            ],
                        },
                    ],
                } satisfies TipTapDoc);
            await shareAnswerMutation.mutateAsync({
                email,
                recipientName: recipient,
                question: askedQuestion,
                answerJson: answerJsonForShare,
                queryType: response.queryType,
                scope: askScope,
                // Include relevant context IDs for the email
                contextResults: response.contextResults,
            });
            enqueueSnackbar("Answer shared successfully!", { variant: "success" });
            setIsShareMode(false);
            setEmail("");
            setRecipient("");
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Failed to share answer.", { variant: "error" });
        }
    };

    const handleCancelShare = () => {
        setIsShareMode(false);
        setEmail("");
        setRecipient("");
        setEmailError("");
    };

    const searches = (latestSearches ?? []) as unknown as SavedSearch[];

    const suggestedQuestions = (
        <div>
            <h3 className="text-md mb-3 font-bold text-gray-700">All Suggested Questions</h3>
            <div className="grid grid-cols-2 gap-5">
                {suggestions.map((suggestion, index) => {
                    const mod = index % 6;
                    const style =
                        mod < 2
                            ? "border-[#B5D6E3] bg-[#E3F2FD] text-[#195D7A]"
                            : mod < 4
                              ? "border-[#7AD2C1] bg-[#D0F5EA] text-[#006659]"
                              : "border-[#8B6BCB] bg-[#F2EAFE] text-[#3E2476]";
                    return (
                        <button
                            key={index}
                            type="button"
                            onClick={() => handleSuggestionClick(suggestion)}
                            disabled={askNavigator.isPending}
                            className={`w-full rounded-lg border p-4 text-left text-sm transition-colors hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 ${style}`}
                        >
                            {suggestion}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                    <div
                        className="flex cursor-pointer items-center gap-1.5 rounded-3xl border border-[#C9C9C9] bg-[#EEEEEE] px-2 py-1.5 text-[#5C6A6D] md:gap-2 md:px-3 lg:min-w-[268px]"
                        role="button"
                        aria-label="Ask navigator"
                    >
                        <AskNavigatorIcon className="shrink-0" />
                        <span className="hidden whitespace-nowrap md:inline">Ask navigator</span>
                    </div>
                </SheetTrigger>
                <SheetContent
                    side="right"
                    className="fixed top-0 bottom-0 h-[calc(100vh)] w-full sm:left-1/2 sm:top-3 sm:bottom-3 sm:h-[calc(100vh-1.5rem)] sm:w-[90vw] sm:max-w-[44rem] sm:-translate-x-1/2 sm:rounded-2xl"
                >
                    <form className="flex h-full flex-col" onSubmit={handleFormSubmit}>
                        <SheetHeader className="gap-y-5 space-y-0">
                            <SheetTitle className="text-left align-middle font-sans text-[24px] font-bold leading-[100%] tracking-[0] text-[#424242]">
                                {isShareMode ? "Share Answer by Email" : "Ask Navigator"}
                            </SheetTitle>
                            <hr className="-mx-[24px] my-7 border-[#c8c8c8]" />
                        </SheetHeader>

                        {!isShareMode && (
                            <div className="flex h-full min-h-0 flex-col">
                                <SheetHeader className="mt-6 mb-4 shrink-0 space-y-3 sm:flex-col">
                                    <div className="w-full">
                                        <TextArea
                                            id="question"
                                            value={question}
                                            onChange={(e) => setQuestion(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    e.preventDefault();
                                                    if (
                                                        question.trim() &&
                                                        !askNavigator.isPending
                                                    ) {
                                                        handleSubmit(question.trim());
                                                    }
                                                }
                                            }}
                                            placeholder="Type your question here..."
                                            disabled={askNavigator.isPending}
                                            className="w-full resize-none rounded-lg border border-gray-300 p-3 focus:border-transparent focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
                                            rows={3}
                                        />
                                    </div>
                                    <div className="!mb-2">
                                        <label className="text-md pr-4 font-bold text-gray-700">
                                            Ask in
                                        </label>
                                        <ToggleGroup
                                            type="single"
                                            value={askScope}
                                            onValueChange={(value) => {
                                                if (value)
                                                    setAskScope(
                                                        value as "dataset" | "internet" | "both",
                                                    );
                                            }}
                                            className="justify-start gap-2"
                                        >
                                            <ToggleGroupItem
                                                value="dataset"
                                                aria-label="Public Navigator Portal"
                                            >
                                                Public Navigator Portal
                                            </ToggleGroupItem>
                                            <ToggleGroupItem value="internet" aria-label="Internet">
                                                Internet
                                            </ToggleGroupItem>
                                            <ToggleGroupItem value="both" aria-label="Both">
                                                Both
                                            </ToggleGroupItem>
                                        </ToggleGroup>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            type="submit"
                                            disabled={!question.trim() || askNavigator.isPending}
                                            variant="primary"
                                            className="flex-1"
                                        >
                                            {askNavigator.isPending
                                                ? "Processing..."
                                                : "Ask Navigator"}
                                            {!askNavigator.isPending && (
                                                <SubmitIcon className="ml-2" />
                                            )}
                                        </Button>
                                        {askNavigator.isPending && (
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                onClick={() => {
                                                    abortController?.abort();
                                                    askNavigator.reset();
                                                    setResponse(null);
                                                    setAskedQuestion(null);
                                                    setAbortController(null);
                                                }}
                                            >
                                                Stop
                                            </Button>
                                        )}
                                    </div>
                                </SheetHeader>

                                <div className="flex-1 shrink overflow-y-auto px-0">
                                    {askNavigator.isPending ? (
                                        <div className="flex h-full items-center justify-center">
                                            <div className="space-y-3 text-center">
                                                <Spinner className="mx-auto" />
                                                <p className="text-sm text-gray-600">Thinking...</p>
                                            </div>
                                        </div>
                                    ) : response ? (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-sm font-medium text-gray-700">
                                                    Answer
                                                </h3>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsShareMode(true)}
                                                        className="flex items-center gap-1 rounded-lg bg-blue-100 px-3 py-1 text-sm text-blue-700 hover:bg-blue-200"
                                                    >
                                                        <Share2 className="h-3 w-3" />
                                                        Share
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setResponse(null);
                                                            setAskedQuestion(null);
                                                        }}
                                                        className="text-brand-alice hover:text-brand-azure text-sm"
                                                    >
                                                        Ask new question
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Asked Question */}
                                            {askedQuestion && (
                                                <div className="rounded-lg border bg-blue-50 p-4">
                                                    <h4 className="mb-2 text-sm font-medium text-blue-900">
                                                        Your Question
                                                    </h4>
                                                    <p className="text-sm text-blue-800">
                                                        {askedQuestion}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Answer (TipTap JSON when present, legacy markdown fallback) */}
                                            {(response.answerJson || response.markdownAnswer) && (
                                                <div className="rounded-lg border bg-gray-50 p-4">
                                                    <h4 className="mb-2 text-sm font-medium text-gray-900">
                                                        Answer
                                                    </h4>
                                                    {response.answerJson ? (
                                                        <ProseDocument doc={response.answerJson} />
                                                    ) : (
                                                        <MarkdownContent
                                                            markdown={response.markdownAnswer ?? ""}
                                                        />
                                                    )}
                                                </div>
                                            )}

                                            {/* AI Suggested Follow-up Questions */}
                                            {response.suggestedQuestions &&
                                                response.suggestedQuestions.length > 0 && (
                                                    <div>
                                                        <h3 className="mb-2 font-bold text-gray-700">
                                                            Suggested Follow-up Questions
                                                        </h3>
                                                        <div className="space-y-2">
                                                            {response.suggestedQuestions.map(
                                                                (sq, i) => (
                                                                    <button
                                                                        key={i}
                                                                        type="button"
                                                                        onClick={() =>
                                                                            handleSuggestionClick(
                                                                                sq,
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            askNavigator.isPending
                                                                        }
                                                                        className="w-full rounded-lg border border-[#B5D6E3] bg-[#E3F2FD] p-3 text-left text-sm text-[#195D7A] transition-colors hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                                                                    >
                                                                        {sq}
                                                                    </button>
                                                                ),
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                            {/* Compass Blog Posts */}
                                            {response.blogPosts &&
                                                response.blogPosts.length > 0 && (
                                                    <div className="space-y-2">
                                                        <span className="inline-flex items-center rounded-full bg-[#F2EAFE] px-3 py-1 text-xs font-medium text-[#3E2476]">
                                                            From the Compass blog
                                                        </span>
                                                        <ul className="space-y-2">
                                                            {response.blogPosts
                                                                .slice(0, 5)
                                                                .map((bp) => {
                                                                    const isVideo =
                                                                        bp.previewKind ===
                                                                            "YOUTUBE_EMBED" ||
                                                                        bp.previewKind ===
                                                                            "VIMEO_EMBED" ||
                                                                        bp.previewKind ===
                                                                            "VIDEO_URL" ||
                                                                        bp.previewKind ===
                                                                            "VIDEO_ASSET";
                                                                    const mediaLabel = isVideo
                                                                        ? bp.previewKind ===
                                                                          "YOUTUBE_EMBED"
                                                                            ? "YouTube"
                                                                            : bp.previewKind ===
                                                                                "VIMEO_EMBED"
                                                                              ? "Vimeo"
                                                                              : "Video"
                                                                        : null;
                                                                    return (
                                                                        <li key={bp.id}>
                                                                            <a
                                                                                href={`/p/${bp.slug}`}
                                                                                className="group flex items-start gap-3 rounded-lg border border-[#D6C7F4] bg-white px-4 py-3 no-underline transition-colors hover:border-[#8B6BCB] hover:bg-[#FAF6FF]"
                                                                            >
                                                                                <div className="min-w-0 flex-1">
                                                                                    <p className="truncate text-sm font-semibold text-[#3E2476] group-hover:text-[#1F0F4E]">
                                                                                        {bp.title}
                                                                                    </p>
                                                                                    {bp.excerpt && (
                                                                                        <p className="line-clamp-2 mt-0.5 text-xs text-gray-600">
                                                                                            {
                                                                                                bp.excerpt
                                                                                            }
                                                                                        </p>
                                                                                    )}
                                                                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                                                        {bp.authorName && (
                                                                                            <span>
                                                                                                by{" "}
                                                                                                {
                                                                                                    bp.authorName
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                        {bp.publishedAt && (
                                                                                            <span>
                                                                                                ·{" "}
                                                                                                {formatISODateRelative(
                                                                                                    bp.publishedAt,
                                                                                                )}
                                                                                            </span>
                                                                                        )}
                                                                                        {mediaLabel && (
                                                                                            <span className="inline-flex items-center rounded-full bg-[#FFE9F0] px-2 py-0.5 text-[10px] font-medium text-[#A8336A]">
                                                                                                {
                                                                                                    mediaLabel
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                        {bp.categories
                                                                                            .slice(
                                                                                                0,
                                                                                                2,
                                                                                            )
                                                                                            .map(
                                                                                                (
                                                                                                    c,
                                                                                                ) => (
                                                                                                    <span
                                                                                                        key={
                                                                                                            c.slug
                                                                                                        }
                                                                                                        className="inline-flex items-center rounded-full bg-[#EEF2F7] px-2 py-0.5 text-[10px] font-medium text-[#475569]"
                                                                                                    >
                                                                                                        {
                                                                                                            c.name
                                                                                                        }
                                                                                                    </span>
                                                                                                ),
                                                                                            )}
                                                                                    </div>
                                                                                </div>
                                                                            </a>
                                                                        </li>
                                                                    );
                                                                })}
                                                        </ul>
                                                    </div>
                                                )}

                                            {/* Vendor Results */}
                                            {response &&
                                                ((response.queryType === "vendor_info" &&
                                                    (response.vendorParams?.vendors?.length ?? 0) >
                                                        0) ||
                                                    response.contextResults.some(
                                                        (result) => result.entityType === "VENDOR",
                                                    )) && (
                                                    <div className="space-y-2">
                                                        <span className="inline-flex items-center rounded-full bg-[#e0f2f7] px-3 py-1 text-xs font-medium text-[#00647a]">
                                                            From your vendor database
                                                        </span>
                                                        <div className="flex flex-wrap gap-2">
                                                            {(
                                                                response.vendorParams?.vendors?.slice(
                                                                    0,
                                                                    3,
                                                                ) ??
                                                                response.contextResults
                                                                    .filter(
                                                                        (r) =>
                                                                            r.entityType ===
                                                                            "VENDOR",
                                                                    )
                                                                    .slice(0, 3)
                                                                    .map((r) => ({
                                                                        id: Number(r.id),
                                                                        name: r.name,
                                                                    }))
                                                            ).map((vendor) => (
                                                                <button
                                                                    key={vendor.id}
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setSelectedVendorId(
                                                                            vendor.id,
                                                                        )
                                                                    }
                                                                    className="inline-flex items-center rounded-full border border-[#0096B8] bg-white px-3 py-1.5 text-xs text-[#0096B8] shadow-sm transition-colors hover:bg-[#e0f2f7]"
                                                                >
                                                                    {vendor.name}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                            {suggestedQuestions}
                                        </div>
                                    ) : (
                                        <div className="space-y-6">
                                            {/* Latest Searches */}
                                            {searches.length > 0 && (
                                                <div>
                                                    <h3 className="text-md mb-3 flex items-center gap-2 font-bold text-gray-700">
                                                        <Clock className="h-4 w-4 text-gray-400" />
                                                        Recent Searches
                                                    </h3>
                                                    <ul className="space-y-2">
                                                        {searches.map((search) => (
                                                            <li key={search.id}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        handleRestoreSearch(search)
                                                                    }
                                                                    disabled={
                                                                        askNavigator.isPending
                                                                    }
                                                                    className="group flex w-full items-start gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                                >
                                                                    {/* Icon */}
                                                                    <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 group-hover:text-gray-500" />

                                                                    {/* Text */}
                                                                    <div className="min-w-0 flex-1">
                                                                        <p className="truncate text-sm font-medium text-gray-800 group-hover:text-gray-900">
                                                                            {search.question}
                                                                        </p>
                                                                        <div className="mt-0.5 flex items-center gap-2">
                                                                            {/* Scope badge */}
                                                                            <span
                                                                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                                                                    search.scope ===
                                                                                    "internet"
                                                                                        ? "bg-green-100 text-green-700"
                                                                                        : search.scope ===
                                                                                            "both"
                                                                                          ? "bg-purple-100 text-purple-700"
                                                                                          : "bg-blue-100 text-blue-700"
                                                                                }`}
                                                                            >
                                                                                {search.scope ===
                                                                                "dataset"
                                                                                    ? "Portal"
                                                                                    : search.scope ===
                                                                                        "internet"
                                                                                      ? "Internet"
                                                                                      : "Both"}
                                                                            </span>
                                                                            {/* Timestamp */}
                                                                            <span className="text-xs text-gray-400">
                                                                                {formatISODateRelative(
                                                                                    search.askedAt,
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {suggestedQuestions}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {isShareMode && (
                            <div className="mt-6">
                                {/* Share Mode UI */}
                                <div className="flex-1 overflow-auto px-0">
                                    <div className="space-y-4">
                                        {/* Preview of what will be shared */}
                                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                            <p className="mb-2 text-xs font-semibold text-gray-600">
                                                Question:
                                            </p>
                                            <p className="mb-3 text-sm text-gray-800">
                                                {askedQuestion}
                                            </p>
                                            <p className="text-xs font-semibold text-gray-600">
                                                The recipient will receive the complete answer in
                                                the email.
                                            </p>
                                        </div>

                                        {/* Email Input */}
                                        <div>
                                            <label className="mb-1 block text-sm font-medium text-gray-700">
                                                Recipient Email *
                                            </label>
                                            <input
                                                type="email"
                                                placeholder="Enter email address"
                                                value={email}
                                                onChange={(e) => {
                                                    setEmail(e.target.value);
                                                    setEmailError("");
                                                }}
                                                className={`w-full rounded-lg border bg-[#eeeeee] px-3 py-2 text-sm outline-none ${
                                                    emailError
                                                        ? "border-red-500 focus:ring-red-500"
                                                        : "border-gray-300 focus:ring-blue-500"
                                                }`}
                                            />
                                            {emailError && (
                                                <p className="mt-1 text-sm text-red-500">
                                                    {emailError}
                                                </p>
                                            )}
                                        </div>

                                        {/* Recipient Name Input */}
                                        <div>
                                            <label className="mb-1 block text-sm font-medium text-gray-700">
                                                Recipient Name (Optional)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter recipient name"
                                                value={recipient}
                                                onChange={(e) => setRecipient(e.target.value)}
                                                className="w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Share Actions */}
                                <div className="mt-6 flex justify-end gap-2">
                                    <Button
                                        onClick={handleCancelShare}
                                        variant="secondary"
                                        type="button"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleShareByEmail}
                                        variant="primary"
                                        type="button"
                                        disabled={shareAnswerMutation.isPending}
                                    >
                                        {shareAnswerMutation.isPending ? "Sharing..." : "Share"}
                                        {!shareAnswerMutation.isPending && (
                                            <SubmitIcon className="ml-2" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </form>
                </SheetContent>
            </Sheet>

            {/* Vendor Details Modal */}
            {vendorData && (
                <VendorDetailedEntry
                    vendor={vendorData}
                    isOpen={selectedVendorId !== null}
                    setIsOpen={(open) => !open && setSelectedVendorId(null)}
                />
            )}
        </div>
    );
};
